<script>
import { onMount } from 'svelte';
import { imageStore } from '../stores';
import { getAuthHeaders } from '../utils/apiClient';

/* ------------------------------------------------------------------
   ⚙️  사용자 환경
   ------------------------------------------------------------------
   - "순수 Svelte + Vite" 또는 간단한 Webpack/Bundler를 사용한다고 가정합니다.
   - 정적 자산은 빌드 도구의 **public/** (Vite) 또는 dev‑server의 정적 폴더에 두어야
     런타임 경로 "/image_A/A/파일명" 으로 서빙됩니다.
   ------------------------------------------------------------------ */

// 상태 변수 ---------------------------------------------
let imageFiles       = [];  // 대기 중/진행 중/완료된 이미지 객체들
let processedImages  = [];
let isProcessing     = false;
let progress         = 0;
let totalImages      = 0;
let successCount     = 0;
let errorCount       = 0;
let processingLog    = [];
let maxThreads       = 8;  // 동시 처리 제한
let processingStart  = null;
let processingEnd    = null;
let processingDurStr = '';

// 🟢 API 서버 주소
import { API_BASE_URL } from '../utils/config';

// 🟢 웹 서버에서 정적 제공되는 이미지 루트
const STATIC_IMAGE_ROOT = '/image_A';

/* 스레드 ID를 동시에 8 개 요청하여 수집하는 디버그용 함수 */
async function sampleThreads() {
  const N = 8;                          // 동시 요청 수
  const t0 = performance.now();

  // ① 동시에 N개 발사
  const results = await Promise.all(
    Array.from({ length: N }, () =>
      fetch(`${API_BASE_URL}/api/thread_echo`).then(r => r.json())
    )
  );

  // ② 총 소요·겹치는지·스레드명 중복 여부 출력
  const dt   = (performance.now() - t0).toFixed(0);
  const ids  = results.map(r => r.thread);
  const uniq = [...new Set(ids)];

  console.table(results);
  alert(
    `요청 ${N}개를 한꺼번에 보냈고 ${dt} ms 만에 모두 돌아옴\n` +
    `스레드 ID들: ${ids.join(', ')}\n` +
    `고유 스레드 수: ${uniq.length}`
  );
}

/* =============== 1. 유틸리티 =============== */
function shuffleArray(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

function addToLog(msg) {
  processingLog = [
    ...processingLog,
    { time: new Date().toLocaleTimeString(), message: msg }
  ].slice(-100);
}

function fmtDuration(ms) {
  const s = Math.floor(ms / 1000) % 60;
  const m = Math.floor(ms / 60000) % 60;
  const h = Math.floor(ms / 3600000);
  return `${h ? h + '시간 ' : ''}${m ? m + '분 ' : ''}${s}초`;
}

/* =============== 2. 이미지 목록 생성 =============== */
async function fetchImageList(start = 1, count = 10, ext = 'png') { // 여기서 start, count 값을 조절해가며 이미지 개수 조절, 함수 호출부분에는 옵션 값이 비어있어야 함
  try {
    // const names = Array.from({ length: count }, (_, i) => `A_${start + i}.${ext}`);
    const names = Array.from({ length: count }, (_, i) => `A_${start + i}.${ext}`);
    imageFiles = names.map((n) => ({ name: n, status: 'pending', resultUrl: null }));
    totalImages = imageFiles.length;
    shuffleArray(imageFiles);
    return true;
  } catch (err) {
    console.error(err);
    alert('이미지 목록 생성 실패: ' + err.message);
    return false;
  }
}

/* =============== 3. 실제 이미지 로드  =============== */
async function getImageFile(fileName) {
  try {
    // 1) 경로 인코딩 → 공백/한글/특수문자 안전
    const url = `${STATIC_IMAGE_ROOT}/${encodeURIComponent(fileName)}`;

    // 2) 이미지 Fetch (Accept 헤더 지정)
    const res = await fetch(url, {
      cache: 'no-store',
      headers: { Accept: 'image/*', 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
    });

    // 3) 검증
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const ctype = res.headers.get('Content-Type') || '';
    if (!ctype.startsWith('image/'))
      throw new Error(`이미지가 아닌 응답 (Content-Type: ${ctype})`);

    // 4) Blob → File
    const blob = await res.blob();
    if (blob.size < 1000) throw new Error(`이미지 크기 비정상 (${blob.size} bytes)`);
    return new File([blob], fileName, { type: ctype });
  } catch (err) {
    addToLog(`❌ 이미지 로드 실패: ${fileName} – ${err.message}`);
    throw err;
  }
}

/* =============== 4. 배경 제거 API 호출 =============== */
async function processImage(img) {
  img.status = 'processing';
  addToLog(`🔄 처리 시작: ${img.name}`);

  try {
    const file = await getImageFile(img.name);
    const fd = new FormData();
    fd.append('image_file', file);
    fd.append('output_format', 'png');

    const headers = await getAuthHeaders();
    const res  = await fetch(`${API_BASE_URL}/api/remove_background`, { method: 'POST', headers, body: fd });
    const data = await res.json();

    if (res.ok) {
      img.status   = 'success';
      img.resultUrl = `data:image/png;base64,${data.result_image_b64}`;
      successCount++;
      addToLog(`✅ 성공: ${img.name}`);
    } else {
      throw new Error(data.error || '배경 제거 실패');
    }
  } catch (err) {
    img.status = 'error';
    errorCount++;
    addToLog(`❌ 실패: ${img.name} - ${err.message}`);
  }

  return img;
}

/* =============== 5. 배치 프로세싱 =============== */
async function processBatch() {
  if (isProcessing) return;
  if (!imageFiles.length && !(await fetchImageList(1, 100))) return;

  isProcessing    = true;
  processingStart = new Date();
  progress        = successCount = errorCount = 0;
  processingLog   = [];

  addToLog(`🚀 배치 시작 - 총 ${totalImages}개, 동시 ${maxThreads}개 스레드`);

  const q   = [...imageFiles];
  const act = new Set();
  processedImages = [];

  while (q.length || act.size) {
    while (q.length && act.size < maxThreads) {
      const img = q.shift();
      const p   = processImage(img).then((r) => {
        act.delete(p);
        processedImages = [...processedImages, r];
        progress = Math.round((processedImages.length / totalImages) * 100);
        processingDurStr = fmtDuration(Date.now() - processingStart);
      });
      act.add(p);
    }
    if (act.size) await Promise.race([...act]);
  }

  processingEnd    = new Date();
  processingDurStr = fmtDuration(processingEnd - processingStart);
  addToLog(`✨ 완료 - 성공 ${successCount}, 실패 ${errorCount}, 소요 ${processingDurStr}`);
  isProcessing = false;

  const first = processedImages.find((i) => i.status === 'success');
  if (first?.resultUrl) imageStore.update((s) => ({ ...s, resultImage: first.resultUrl }));
}

/* =============== 6. 초기 로드 =============== */
onMount(fetchImageList);
</script>

<!-- =============== 7. UI 부분 (마크업 & 스타일) =============== -->
<div class="batch-processor">
  <h2>이미지 배치 처리기</h2>
  <div class="controls">
    <button disabled={isProcessing} on:click={processBatch} class="primary-button">
      실제 이미지 100장 배경 제거 실행
    </button>
    <button disabled={isProcessing} on:click={sampleThreads} class="primary-button">멀티스레드 증명 테스트</button>
    <div class="thread-control">
      <label for="maxThreads">동시 처리 스레드 수:</label>
      <input id="maxThreads" type="range" bind:value={maxThreads} min="1" max="32" disabled={isProcessing} />
      <span>{maxThreads}</span>
    </div>
  </div>

  {#if isProcessing || processedImages.length}
    <div class="progress-container">
      <div class="progress-bar"><div class="progress-fill" style="width: {progress}%"></div></div>
      <div class="progress-text">{progress}% 완료 ({processedImages.length}/{totalImages})</div>
      <div class="status-summary">
        <span class="success">성공: {successCount}</span>
        <span class="error">실패: {errorCount}</span>
        {#if processingDurStr}<span class="duration">처리 시간: {processingDurStr}</span>{/if}
      </div>
    </div>
  {/if}

  <div class="log-container">
    <h3>처리 로그</h3>
    <div class="log">
      {#each processingLog as e}
        <div class="log-entry"><span class="log-time">{e.time}</span><span class="log-message">{e.message}</span></div>
      {/each}
    </div>
  </div>
</div>

<style>
  /* 스타일은 필요에 따라 그대로 사용하거나 수정 */
  .batch-processor { padding: 20px; max-width: 900px; margin: auto; }
  .controls { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; margin-bottom: 20px; }
  .primary-button { background: #4caf50; color: #fff; border: none; padding: 10px 22px; font-size: 15px; border-radius: 4px; cursor: pointer; }
  .primary-button:hover { background: #39843b; }
  .primary-button:disabled { background: #9e9e9e; cursor: not-allowed; }
  .thread-control { display: flex; align-items: center; gap: 8px; }
  .progress-bar { width: 100%; height: 20px; border-radius: 10px; overflow: hidden; }
  :global(.dark) .progress-bar { background: #444; }
  :global(.light) .progress-bar { background: #f0f0f0; }
  .progress-fill { height: 100%; background: #00b7ff; transition: width 0.3s; }
  .progress-text { text-align: center; margin: 6px 0 12px; font-size: 14px; }
  .status-summary { display: flex; justify-content: center; gap: 20px; }
  .success { color: #4caf50; font-weight: 600; }
  .error { color: #f44336; font-weight: 600; }
  .duration { color: #2196f3; font-weight: 600; }
  .log-container { margin-top: 20px; border-radius: 4px; padding: 10px; }
  .log { height: 200px; overflow-y: auto; font-family: 'Pretendard Medium'; font-size: 13px; padding: 6px; }
  .log-entry { padding: 2px 0; line-height: 1.4; border-bottom: 1px solid; }
  .log-time { margin-right: 8px; }

  :global(.dark) .log-container { background: #2a2a2a; border: 1px solid #444; }
  :global(.light) .log-container { background: #fafafa; border: 1px solid #ddd; }

  :global(.dark) .log { background: #1a1a1a; color: #f5f5f5; }
  :global(.light) .log { background: #fff; color: #404040; }

  :global(.dark) .log-entry { border-bottom-color: rgba(255, 255, 255, 0.2); }
  :global(.light) .log-entry { border-bottom-color: rgba(0, 0, 0, 0.2); }

  :global(.dark) .log-time { color: #f5f5f5; }
  :global(.light) .log-time { color: #404040; }
</style>
